import type { Applicant, Application, Employee, JobOpening, Offer } from "@shared/schema";
import { Check, Copy, Eye, Send, Trash2, X } from "lucide-react";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/constants";

type OfferWithDetails = Offer & {
  application?: Application & { applicant?: Applicant; jobOpening?: JobOpening };
  creator?: Employee;
  approver?: Employee;
};

type StageTone = "coral" | "forest" | "amber" | "muted";

interface KanbanColumn {
  id: string;
  title: string;
  tone: StageTone;
  statuses: string[];
}

const TONE_DOT: Record<StageTone, string> = {
  coral: "bg-[hsl(var(--comp-coral))]",
  forest: "bg-[hsl(var(--forest-pulse))]",
  amber: "bg-[hsl(var(--amber-state))]",
  muted: "bg-[hsl(var(--ink-muted))]",
};

const columns: KanbanColumn[] = [
  {
    id: "waiting_approval",
    title: "Aprovação pendente",
    tone: "coral",
    statuses: ["waiting_approval"],
  },
  { id: "approved", title: "Aprovadas", tone: "forest", statuses: ["approved"] },
  { id: "sent", title: "Enviadas", tone: "amber", statuses: ["sent"] },
  { id: "accepted", title: "Aceitas", tone: "forest", statuses: ["accepted"] },
  {
    id: "rejected",
    title: "Recusadas e canceladas",
    tone: "muted",
    statuses: ["rejected", "declined", "cancelled"],
  },
];

function KanbanOfferCard({
  offer,
  onApprove,
  onReject,
  onSend,
  onDelete,
  onMarkAccepted,
  copyOfferLink,
  viewPreview,
}: {
  offer: OfferWithDetails;
  onApprove?: (offer: OfferWithDetails) => void;
  onReject?: (offer: OfferWithDetails) => void;
  onSend?: (offer: OfferWithDetails) => void;
  onDelete: (offer: OfferWithDetails) => void;
  onMarkAccepted?: (offer: OfferWithDetails) => void;
  copyOfferLink: (token: string) => void;
  viewPreview: (token: string) => void;
}) {
  const salaryChanged =
    offer.acceptedSalaryBase != null &&
    Math.abs(parseFloat(offer.acceptedSalaryBase) - parseFloat(offer.salaryBase)) > 0.01;
  const equityChanged =
    offer.acceptedEquityShares != null && offer.acceptedEquityShares !== (offer.equityShares ?? 0);

  return (
    <div
      data-testid={`kanban-card-offer-${offer.id}`}
      className="bg-[hsl(var(--paper-white))] border border-[hsl(var(--sand-divider))] rounded-md p-3 transition-shadow hover:shadow-[0_2px_6px_rgba(0,0,0,0.04)]"
    >
      <div className="flex items-center justify-between gap-1 min-w-0">
        <p className="font-semibold text-[13px] leading-tight truncate min-w-0 text-[hsl(var(--ink-primary))]">
          {offer.application?.applicant?.fullName || "Candidato"}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--alert-red))] flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(offer);
          }}
          data-testid={`button-delete-offer-kanban-${offer.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      <p className="text-[11.5px] text-[hsl(var(--ink-muted))] mt-1 truncate">
        {offer.application?.jobOpening?.title || "-"}
      </p>

      <div className="mt-2 space-y-1 text-[12px] tabular-nums">
        <div className="flex items-center justify-between">
          <span className="text-[hsl(var(--ink-muted))]">Salário</span>
          {salaryChanged ? (
            <div className="text-right">
              <span className="font-medium text-[hsl(var(--ink-primary))]">
                {formatCurrency(parseFloat(offer.acceptedSalaryBase!))}/mês
              </span>
              <span className="text-[hsl(var(--ink-muted))] line-through ml-1">
                {formatCurrency(parseFloat(offer.salaryBase))}
              </span>
            </div>
          ) : (
            <span className="font-medium text-[hsl(var(--ink-primary))]">
              {formatCurrency(parseFloat(offer.salaryBase))}/mês
            </span>
          )}
        </div>

        {offer.equityShares || offer.acceptedEquityShares ? (
          <div className="flex items-center justify-between">
            <span className="text-[hsl(var(--ink-muted))]">Equity</span>
            {equityChanged ? (
              <div className="text-right">
                <span className="font-medium text-[hsl(var(--ink-primary))]">
                  {offer.acceptedEquityShares!.toLocaleString("pt-BR")} shares
                </span>
                <span className="text-[hsl(var(--ink-muted))] line-through ml-1">
                  {(offer.equityShares ?? 0).toLocaleString("pt-BR")}
                </span>
              </div>
            ) : (
              <span className="font-medium text-[hsl(var(--ink-primary))]">
                {offer.equityShares ?? 0} shares
              </span>
            )}
          </div>
        ) : null}

        {offer.signingBonus && (
          <div className="flex items-center justify-between">
            <span className="text-[hsl(var(--ink-muted))]">Signing bonus</span>
            <span className="font-medium text-[hsl(var(--ink-primary))]">
              {formatCurrency(parseFloat(offer.signingBonus))}
            </span>
          </div>
        )}
      </div>

      {offer.creator && offer.status === "waiting_approval" && (
        <p className="text-[11.5px] text-[hsl(var(--ink-muted))] mt-2">
          Criado por {offer.creator.fullName}
        </p>
      )}

      {offer.approver && offer.status === "approved" && (
        <p className="text-[11.5px] text-[hsl(var(--ink-muted))] mt-2">
          Aprovado por {offer.approver.fullName}
        </p>
      )}

      {offer.status === "waiting_approval" && onApprove && onReject && (
        <div className="flex gap-2 mt-3">
          <Button
            onClick={() => onApprove(offer)}
            className="flex-1 h-7 text-xs"
            size="sm"
            data-testid={`button-approve-offer-${offer.id}`}
          >
            <Check className="w-3 h-3 mr-1" />
            Aprovar
          </Button>
          <Button
            variant="destructive"
            onClick={() => onReject(offer)}
            className="flex-1 h-7 text-xs"
            size="sm"
            data-testid={`button-reject-offer-${offer.id}`}
          >
            <X className="w-3 h-3 mr-1" />
            Recusar
          </Button>
        </div>
      )}

      {offer.status === "approved" && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {offer.previewToken && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => viewPreview(offer.previewToken!)}
            >
              <Eye className="w-3 h-3 mr-1" />
              Preview
            </Button>
          )}
          {offer.acceptanceToken && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => copyOfferLink(offer.acceptanceToken)}
            >
              <Copy className="w-3 h-3 mr-1" />
              Link
            </Button>
          )}
          {onSend && (
            <Button onClick={() => onSend(offer)} size="sm" className="h-7 text-xs">
              <Send className="w-3 h-3 mr-1" />
              Enviar
            </Button>
          )}
        </div>
      )}

      {(offer.status === "sent" || offer.status === "accepted" || offer.status === "declined") &&
        offer.acceptanceToken && (
          <div className="flex gap-2 mt-3 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => copyOfferLink(offer.acceptanceToken)}
            >
              <Copy className="w-3 h-3 mr-1" />
              Link
            </Button>
            {offer.previewToken && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => viewPreview(offer.previewToken!)}
              >
                <Eye className="w-3 h-3 mr-1" />
                Preview
              </Button>
            )}
          </div>
        )}

      {offer.status === "sent" && onMarkAccepted && (
        <Button
          onClick={() => onMarkAccepted(offer)}
          size="sm"
          className="w-full h-7 text-xs mt-2"
          data-testid={`button-mark-accepted-offer-${offer.id}`}
        >
          <Check className="w-3 h-3 mr-1" />
          Marcar como aceita
        </Button>
      )}

      {(offer.status === "declined" ||
        offer.status === "rejected" ||
        offer.status === "cancelled") && (
        <div className="mt-2">
          <StatusBadge status={offer.status} />
        </div>
      )}
    </div>
  );
}

export function OffersKanbanView({
  offers,
  onApprove,
  onReject,
  onSend,
  onDelete,
  onMarkAccepted,
  copyOfferLink,
  viewPreview,
}: {
  offers: OfferWithDetails[];
  onApprove: (offer: OfferWithDetails) => void;
  onReject: (offer: OfferWithDetails) => void;
  onSend: (offer: OfferWithDetails) => void;
  onDelete: (offer: OfferWithDetails) => void;
  onMarkAccepted?: (offer: OfferWithDetails) => void;
  copyOfferLink: (token: string) => void;
  viewPreview: (token: string) => void;
}) {
  const offersByColumn = (col: KanbanColumn) =>
    offers.filter((o) => col.statuses.includes(o.status));

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="offers-kanban-view">
      <div
        className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
        style={{ minHeight: "400px" }}
      >
        <div className="flex gap-2.5 min-w-max h-full pb-4">
          {columns.map((column) => {
            const columnOffers = offersByColumn(column);
            return (
              <div
                key={column.id}
                className="w-72 flex-shrink-0 flex flex-col h-full"
                data-testid={`kanban-column-${column.id}`}
              >
                <div className="bg-[hsl(var(--paper-2))] border border-[hsl(var(--sand-divider))] rounded-md flex flex-col h-full">
                  <div className="flex items-center justify-between px-3 py-2.5 border-b border-[hsl(var(--sand-divider))] flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`w-2 h-2 rounded-full ${TONE_DOT[column.tone]} flex-shrink-0`}
                      />
                      <h3 className="font-semibold text-[12px] text-[hsl(var(--ink-primary))] truncate">
                        {column.title}
                      </h3>
                    </div>
                    <span className="text-[11px] tabular-nums text-[hsl(var(--ink-muted))]">
                      {columnOffers.length}
                    </span>
                  </div>

                  <div className="p-2 space-y-1.5 min-h-[80px] flex-1 overflow-y-auto">
                    {columnOffers.map((offer) => (
                      <KanbanOfferCard
                        key={offer.id}
                        offer={offer}
                        onApprove={onApprove}
                        onReject={onReject}
                        onSend={onSend}
                        onDelete={onDelete}
                        onMarkAccepted={onMarkAccepted}
                        copyOfferLink={copyOfferLink}
                        viewPreview={viewPreview}
                      />
                    ))}
                    {columnOffers.length === 0 && (
                      <div className="flex items-center justify-center h-20 text-[11.5px] text-[hsl(var(--ink-muted))] border border-dashed border-[hsl(var(--sand-divider))] rounded-md">
                        Nenhuma oferta
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
